<script lang="ts" setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import type { ChatTemplate } from "@/api/chat/dto/chat-template";

withDefaults(
  defineProps<{
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits<{
  (e: "select", value: ChatTemplate): void;
}>();

const { t } = useI18n();
const api = new API();

const isMenuOpen = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const templates = ref<ChatTemplate[]>([]);

async function loadTemplates() {
  if (isLoading.value || templates.value.length) {
    return;
  }

  isLoading.value = true;

  try {
    const resp = await api.chat.getTemplates();

    if (resp.success && resp.data) {
      templates.value = resp.data;
    }
  } catch (error) {
    console.error("Can not load chat templates", error);
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <v-menu v-model="isMenuOpen" location="top start">
    <template #activator="{ props: menuProps }">
      <v-icon :disabled="disabled" v-bind="menuProps" @click="loadTemplates">mdi-note-text-outline</v-icon>
    </template>

    <v-list
      class="py-3 templates-list"
      color="primary"
      density="compact"
      max-height="330"
      max-width="600"
      min-width="240"
    >
      <v-list-subheader class="font-size-14 text-grey-navy font-weight-600 py-3">{{
        t("chats.templates")
      }}</v-list-subheader>

      <v-list-item v-if="isLoading" class="text-center">
        <v-progress-circular color="primary" indeterminate size="20" />
      </v-list-item>

      <v-list-item v-else-if="!templates.length">
        <v-list-item-title class="text-grey-darken-1">{{ t("chats.no-templates") }}</v-list-item-title>
      </v-list-item>

      <v-list-item v-for="template in templates" :key="template.id" class="py-2" @click="emit('select', template)">
        <v-list-item-title class="font-size-14 text-grey-navy font-weight-600">{{ template.name }}</v-list-item-title>

        <div class="font-size-13 text-grey-darken-1">{{ template.text }}</div>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<style lang="scss" scoped>
.templates-list {
  :deep(.v-list-item:hover) {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
